import { ReactNode, useState, useEffect, useRef, useCallback, ComponentType } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate, useLocation } from 'react-router-dom';
import PullToRefresh from 'react-simple-pull-to-refresh';
import { supabase } from './supabase';
import Img from '../components/ui/Img';
import { getLogoPath } from '../utils/theme';
import { ChevronDown, History, LogOut, Menu, X, Shield, UserCircle, PanelLeft, PanelLeftClose } from 'lucide-react';
import { StaffBottomNav } from './components/StaffBottomNav';
import { useStaffPillars } from './useStaffPillars';
import { activePillarId } from './staffPillars';

interface StaffUser {
  id: string;
  email: string;
  name: string | null;
  program_ids: string[] | null;
  virtual_class_keys?: string[] | null;
}

interface Props {
  children: ReactNode;
  staffUser: StaffUser;
}

type NavItem = {
  key: string;
  label: string;
  icon: ComponentType<{ className?: string }>;
  /** External portal link (full page load). */
  href?: string;
  /** In-app path; defaults to `/staff-app/<key>`. */
  to?: string;
};

/**
 * The menu is the four pillars — After School, Enrichment, Chinese, Math — built from the
 * rosters this teacher actually has today (see `staffPillars.ts`).
 *
 * It used to be a single "Attendance" entry, which spent the whole menu on a word no
 * teacher needs to be told, while the choice they *do* make lived in an in-page tab strip
 * that could run to eighteen entries. The pillars move that first choice into the menu and
 * leave the tabs for choosing within a category.
 *
 * Overview, Students and Schedule still exist as routes (see StaffApp) but are
 * intentionally omitted — adding an entry back is all it takes to re-expose one.
 *
 * History sits below the pillars, behind a rule. It is not a fifth pillar: a pillar is a
 * roster running today, and the archive is every day but today. The rule says so — the
 * pillars are the work in front of you, and what is under the line is a record of what is
 * behind you.
 */

const SIDEBAR_KEY = 'staff-sidebar-open';

/**
 * MOBILE_CHROME_ON_BODY
 *
 * Every `position: fixed` bar on a phone — the header, the bottom nav, the drawer and its
 * backdrop — is rendered into `document.body` through a portal instead of into the tree
 * below, because that tree sits inside `<PullToRefresh>`. The library wraps its children in
 * `.ptr` / `.ptr__children`, which it styles `height: 100%; overflow: hidden` and transforms
 * while you pull.
 *
 * A clipping ancestor is enough for WebKit to stop treating a fixed descendant as
 * viewport-anchored. On iOS these bars were drawn against the top of the *document*
 * instead, so scrolling the roster carried the header off the top of the screen and left
 * the bottom nav stranded across the middle of the list, with student rows running on
 * underneath it. Desktop compositors anchor them correctly either way, which is why
 * narrowing a browser window never reproduced it.
 *
 * Fixed positioning ignores the DOM parent, so a portal changes nothing about where these
 * bars land or how they stack — only which subtree they hang off, and therefore which
 * clips can capture them. The desktop sidebar stays put: there the page itself never
 * scrolls (`h-screen overflow-hidden`, with `<main>` as the scroller), so there is nothing
 * for a fixed bar to drift against.
 */

export function StaffLayout({ children, staffUser }: Props) {
  const location = useLocation();
  const navigate = useNavigate();
  const [isMobile, setIsMobile] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);
  const [isSidebarOpen, setIsSidebarOpen] = useState(() => {
    const stored = localStorage.getItem(SIDEBAR_KEY);
    return stored !== null ? stored === 'true' : true;
  });
  const [isProfileOpen, setIsProfileOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  // Collapsed is a rail, not a disappearance: the icons stay so the menu is still one click
  // away and the page does not reflow to full width and back.
  const isRail = !isMobile && !isSidebarOpen;

  const { pillars } = useStaffPillars(staffUser);
  // Resolved by looking up the group id rather than by parsing the path: an enrichment
  // class's group id is a bare program UUID and says nothing about its category.
  const activePillar = activePillarId(location.pathname, pillars);

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < 768 || /Mobi|Android/i.test(navigator.userAgent));
    check();
    window.addEventListener('resize', check);
    return () => window.removeEventListener('resize', check);
  }, []);

  useEffect(() => { setIsMobileSidebarOpen(false); }, [location.pathname]);

  // Persist sidebar state (desktop only)
  useEffect(() => {
    if (!isMobile) {
      localStorage.setItem(SIDEBAR_KEY, String(isSidebarOpen));
    }
  }, [isSidebarOpen, isMobile]);

  const handleClickOutside = useCallback((e: MouseEvent) => {
    if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
      setIsProfileOpen(false);
    }
  }, []);

  useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [handleClickOutside]);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate('/');
  };

  const handleRefresh = () => {
    window.location.reload();
    return Promise.resolve();
  };

  const [avatarUrl, setAvatarUrl] = useState<string | null>(null);
  const [isAlsoAdmin, setIsAlsoAdmin] = useState(false);
  const [isAlsoParent, setIsAlsoParent] = useState(false);
  const displayName = staffUser.name ?? staffUser.email;

  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => {
      setAvatarUrl(data.session?.user?.user_metadata?.avatar_url ?? null);
    });
    supabase.rpc('is_admin').then(({ data }) => {
      setIsAlsoAdmin(data === true);
    });
    // A teacher only sees the Parent Portal link if they also have a parent
    // entity (matched by email) — we never nudge a non-parent to onboard.
    if (staffUser.email) {
      supabase.from('parents').select('id').eq('email', staffUser.email).maybeSingle()
        .then(({ data }) => setIsAlsoParent(!!data));
    }
  }, [staffUser.email]);

  const portalLinks: NavItem[] = [
    ...(isAlsoAdmin ? [{ key: 'admin-portal', label: 'Admin Portal', icon: Shield, href: '/admin-app' }] : []),
    ...(isAlsoParent ? [{ key: 'parent-portal', label: 'Parent Portal', icon: UserCircle, href: '/dashboard' }] : []),
  ];

  const renderNavItem = ({ key, label, icon: Icon, href, to, active }: NavItem & { active?: boolean }) => {
    const isActive = !href && Boolean(active);
    return (
      <button
        key={key}
        onClick={() => href ? (window.location.href = href) : navigate(to ?? `/staff-app/${key}`)}
        title={isRail ? label : undefined}
        aria-label={isRail ? label : undefined}
        className={`flex items-center w-full py-2 mb-0.5 rounded-lg text-sm transition-colors ${
          isRail ? 'justify-center px-0' : 'px-3'
        } ${
          href
            ? 'bg-indigo-50 text-indigo-700 font-medium hover:bg-indigo-100'
            : isActive
            ? 'bg-gray-100 text-gray-900 font-semibold'
            : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
        }`}
      >
        <Icon className="h-[18px] w-[18px] flex-shrink-0" />
        {!isRail && <span className="ml-3">{label}</span>}
      </button>
    );
  };

  const sidebarContent = (
    <>
      <div className={`flex items-center h-14 border-b border-gray-200 flex-shrink-0 ${isRail ? 'justify-center px-0' : 'px-4'}`}>
        <Img src={getLogoPath()} alt="BHCS Logo" className="h-7 w-auto" width={512} height={512} />
        {!isRail && <span className="ml-2 text-lg font-bold text-gray-900">Teacher Portal</span>}
        {isMobile && (
          <button
            onClick={() => setIsMobileSidebarOpen(false)}
            className="ml-auto p-1 rounded-md text-gray-400 hover:text-gray-600 hover:bg-gray-100"
            aria-label="Close sidebar"
          >
            <X className="h-5 w-5" />
          </button>
        )}
      </div>

      <nav className="flex-1 overflow-y-auto py-2 px-2 flex flex-col">
        <div>
          {/* An empty menu reads as a broken one. On a day nothing meets — a holiday, or a
              teacher between terms — say so, which is also what the roster page says. */}
          {pillars.length === 0 && !isRail && (
            <p className="px-3 py-2 text-sm text-gray-400 italic">No rosters today</p>
          )}
          {pillars.map(pillar =>
            renderNavItem({
              key: pillar.id,
              label: pillar.label,
              icon: pillar.icon,
              to: pillar.path,
              active: pillar.id === activePillar,
            }),
          )}

          <hr className="my-2 mx-1 border-gray-100" />
          {renderNavItem({
            key: 'history',
            label: 'History',
            icon: History,
            to: '/staff-app/history',
            active: location.pathname.startsWith('/staff-app/history'),
          })}
        </div>
        {portalLinks.length > 0 && (
          <div className="mt-auto pt-1">
            <hr className="my-1 mx-1 border-gray-100" />
            {portalLinks.map(renderNavItem)}
          </div>
        )}
      </nav>

      {/* Feature Flag Override Panel */}
      <div className="px-3 py-2 border-t border-gray-200">
      </div>

      <div className={`border-t border-gray-200 flex-shrink-0 ${isRail ? 'p-2' : 'p-3'}`} ref={dropdownRef}>
        <div className="relative">
          <button
            onClick={() => setIsProfileOpen(v => !v)}
            title={isRail ? displayName : undefined}
            className={`flex items-center w-full rounded-lg hover:bg-gray-100 transition-colors ${
              isRail ? 'justify-center p-1' : 'p-2'
            }`}
            aria-haspopup="menu"
            aria-expanded={isProfileOpen}
            aria-label={isRail ? displayName : undefined}
          >
            {avatarUrl ? (
              <Img src={avatarUrl} alt={displayName} className="h-8 w-8 rounded-full flex-shrink-0" width={32} height={32} />
            ) : (
              <div className="h-8 w-8 rounded-full bg-indigo-100 flex items-center justify-center flex-shrink-0">
                <span className="text-sm font-semibold text-indigo-700">{displayName.charAt(0).toUpperCase()}</span>
              </div>
            )}
            {!isRail && (
              <>
                <div className="ml-3 text-left min-w-0 flex-1">
                  <p className="text-sm font-medium text-gray-900 truncate">{displayName}</p>
                  <p className="text-xs text-gray-500 truncate">{staffUser.email}</p>
                </div>
                <ChevronDown className={`h-4 w-4 text-gray-400 flex-shrink-0 transition-transform ${isProfileOpen ? 'rotate-180' : ''}`} />
              </>
            )}
          </button>

          {isProfileOpen && (
            <div className={`absolute bottom-full mb-1 bg-white rounded-lg shadow-lg border border-gray-200 py-1 z-50 ${
              isRail ? 'left-0 w-44' : 'left-0 right-0'
            }`}>
              <button
                onClick={handleSignOut}
                className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50"
              >
                <LogOut className="h-4 w-4 mr-2 text-gray-400" />
                Sign out
              </button>
            </div>
          )}
        </div>
      </div>
    </>
  );

  return (
    <PullToRefresh onRefresh={handleRefresh} isPullable={isMobile}>
      <div className={`${isMobile ? 'min-h-screen' : 'h-screen overflow-hidden'} bg-gray-50 flex`}>
        {/* Desktop sidebar */}
        {!isMobile && (
          <aside
            className={`fixed top-0 left-0 bottom-0 z-40 bg-white border-r border-gray-200 flex flex-col transition-all duration-300 ease-in-out ${
              isSidebarOpen ? 'w-60' : 'w-16'
            } ${isProfileOpen ? '' : 'overflow-hidden'}`}
          >
            {/* Clipping is what keeps the 240px-wide inner wrapper from spilling out while the
                rail animates back open — but it also cropped the profile menu, which is wider
                than the 64px rail it hangs off. Drop the clip while that menu is open: the
                sidebar is not mid-transition then, because opening it closes the menu first. */}
            {/* Inner wrapper keeps the content from squishing mid-transition */}
            <div className={`h-full flex flex-col ${isSidebarOpen ? 'w-60 min-w-[15rem]' : 'w-16 min-w-[4rem]'}`}>
              {sidebarContent}
            </div>
          </aside>
        )}

        {/* Desktop sidebar toggle */}
        {!isMobile && (
          <button
            onClick={() => setIsSidebarOpen(prev => !prev)}
            className={`fixed top-3 z-50 p-2 rounded-lg bg-white border border-gray-200 shadow-sm text-gray-500 hover:text-gray-700 hover:bg-gray-50 transition-all duration-300 ease-in-out ${
              isSidebarOpen ? 'left-[15.5rem]' : 'left-[4.5rem]'
            }`}
            aria-label={isSidebarOpen ? 'Close sidebar' : 'Open sidebar'}
          >
            {isSidebarOpen ? <PanelLeftClose className="h-5 w-5" /> : <PanelLeft className="h-5 w-5" />}
          </button>
        )}

        {/* Mobile backdrop and sidebar — see the note on `MOBILE_CHROME_ON_BODY` */}
        {isMobile && createPortal(
          <>
            {isMobileSidebarOpen && (
              <div
                className="fixed inset-0 z-40 bg-black/30 backdrop-blur-sm"
                onClick={() => setIsMobileSidebarOpen(false)}
              />
            )}
            <aside className={`fixed top-0 left-0 bottom-0 z-50 bg-white w-72 flex flex-col shadow-xl transition-transform duration-300 ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'}`}>
              {sidebarContent}
            </aside>
          </>,
          document.body,
        )}

        {/* Main content */}
        <div
          className={`flex-1 min-w-0 ${
            isMobile
              ? 'overflow-y-auto'
              : `flex flex-col overflow-hidden transition-all duration-300 ease-in-out ${isSidebarOpen ? 'ml-60' : 'ml-16'}`
          }`}
        >
          {/* Mobile header — see the note on `MOBILE_CHROME_ON_BODY` */}
          {isMobile && createPortal(
            <div className="fixed top-0 left-0 right-0 z-30 bg-white border-b border-gray-200 flex items-center h-14 px-4">
              <button
                onClick={() => setIsMobileSidebarOpen(true)}
                className="p-2 -ml-2 rounded-lg text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                aria-label="Open menu"
              >
                <Menu className="h-5 w-5" />
              </button>
              <div className="ml-3 flex items-center">
                <Img src={getLogoPath()} alt="BHCS Logo" className="h-6 w-auto" width={512} height={512} />
                <span className="ml-2 text-base font-bold text-gray-900">Teacher Portal</span>
              </div>
            </div>,
            document.body,
          )}
          <main className={`px-4 sm:px-6 lg:px-10 ${isMobile ? 'pt-20 pb-28' : 'py-6 flex-1 min-h-0 overflow-y-auto'}`}>
            {children}
          </main>
          {/* Bottom nav is the primary navigation on a phone, mirroring the parent portal.
              The hamburger stays for what is not a pillar: profile, sign out, portal links.
              `pb-28` above clears the bar so the last roster row is never trapped under it. */}
          {isMobile && createPortal(
            <StaffBottomNav
              pillars={pillars}
              activeId={activePillar}
              historyActive={location.pathname.startsWith('/staff-app/history')}
            />,
            document.body,
          )}
        </div>
      </div>
    </PullToRefresh>
  );
}
